import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ListToolbar } from "@/components/ListToolbar";
import { Plus, Trash2, UserCog } from "lucide-react";
import { ROLE_LABELS } from "@/lib/roles";

const ROLES = ["supervisor", "director", "registrar", "staff", "student", "qa", "tenant_admin"];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "student", expertise: "" });
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false); setForm({ name: "", email: "", password: "", role: "student", expertise: "" }); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Users & Roles</h1>
          <p className="text-muted-foreground">Manage supervisors, staff, students and QA within your institution.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-user-btn" className="brand-bg text-white"><Plus className="h-4 w-4 mr-1" />Add User</Button></DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Email</Label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Password</Label><Input data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.role === "supervisor" && <div><Label>Expertise</Label><Input data-testid="user-expertise" value={form.expertise} onChange={(e) => setForm({ ...form, expertise: e.target.value })} placeholder="e.g. Public Health, Qualitative" /></div>}
            </div>
            <DialogFooter><Button data-testid="user-create" onClick={create} className="brand-bg text-white">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-2" data-testid="users-list">
        {users.length > 0 && (
          <ListToolbar
            testid="users"
            search={q}
            onSearch={setQ}
            placeholder="Search by name or email…"
            filters={[{
              key: "role", value: roleFilter, onChange: setRoleFilter, label: "Role",
              options: [{ value: "all", label: "All roles" }, ...ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] }))],
            }]}
          />
        )}
        {users
          .filter((u) => roleFilter === "all" || u.tenant_role === roleFilter)
          .filter((u) => {
            const t = q.trim().toLowerCase();
            return !t || [u.name, u.email].some((x) => (x || "").toLowerCase().includes(t));
          })
          .map((u) => (
          <Card key={u.id} className="p-4 flex items-center justify-between" data-testid={`user-${u.id}`}>
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-md bg-secondary flex items-center justify-center brand-text"><UserCog className="h-4 w-4" /></div>
              <div>
                <p className="font-medium text-sm">{u.name}</p>
                <p className="text-xs text-muted-foreground">{u.email}{u.expertise ? ` · ${u.expertise}` : ""}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline">{ROLE_LABELS[u.tenant_role]}</Badge>
              <Button size="icon" variant="ghost" className="text-destructive h-8 w-8" data-testid={`del-user-${u.id}`} onClick={() => del(u.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
